import { useState, useEffect, useCallback } from "react";
import {
  queueSubmission,
  syncAllPending,
  getPendingCount,
} from "@/lib/offlineSync";
import { toast } from "sonner";

interface UseOfflineSyncOptions {
  showToasts?: boolean;
}

export function useOfflineSync(options: UseOfflineSyncOptions = {}) {
  const { showToasts = true } = options;
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Update pending count
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await getPendingCount();
      setPendingCount(count);
    } catch (error) {
      console.error("[useOfflineSync] Failed to get pending count:", error);
    }
  }, []);

  // Sync pending submissions
  const sync = useCallback(async () => {
    if (!navigator.onLine || isSyncing) return;

    setIsSyncing(true);
    try {
      const { synced, failed } = await syncAllPending();
      await refreshPendingCount();

      if (showToasts && synced > 0) {
        toast.success(`Synced ${synced} pending submission${synced > 1 ? "s" : ""}`);
      }
      if (showToasts && failed > 0) {
        toast.error(`Failed to sync ${failed} submission${failed > 1 ? "s" : ""}`);
      }
    } catch (error) {
      console.error("[useOfflineSync] Sync failed:", error);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refreshPendingCount, showToasts]);

  // Handle online/offline status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (showToasts) {
        toast.success("You're back online!");
      }
      // Auto-sync when coming back online
      sync();
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (showToasts) {
        toast.warning("You're offline. Submissions will be queued.");
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial count
    refreshPendingCount();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [sync, refreshPendingCount, showToasts]);

  // Submit with offline support
  const submitWithOfflineSupport = useCallback(
    async (
      url: string,
      options: RequestInit
    ): Promise<{ success: boolean; queued: boolean; response?: Response }> => {
      if (!navigator.onLine) {
        // Queue for later
        await queueSubmission(url, options);
        await refreshPendingCount();
        
        if (showToasts) {
          toast.info("You're offline. Your submission has been queued.");
        }
        
        return { success: true, queued: true };
      }

      try {
        const response = await fetch(url, options);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        return { success: true, queued: false, response };
      } catch (error) {
        // Network error - queue for later
        console.error("[useOfflineSync] Request failed, queueing:", error);
        await queueSubmission(url, options);
        await refreshPendingCount();
        
        if (showToasts) {
          toast.info("Request failed. Queued for retry when online.");
        }
        
        return { success: true, queued: true };
      }
    },
    [refreshPendingCount, showToasts]
  );

  return {
    isOnline,
    pendingCount,
    isSyncing,
    sync,
    submitWithOfflineSupport,
    refreshPendingCount,
  };
}
